import React, { useContext, useRef, useState } from "react";
import styles from "./styles.module.css";
import SyntaxHighlighter from '../../../components/SyntaxHighlighter'
import { FaPlay, FaPause } from "react-icons/fa";
import {
  colorsType,
  data,
  highlightedRange,
  lineChartBiColor,
  multiColorCustomGradientLineChart,
  specialCode,
  specialData,
} from "./data";
import { data as areaData } from "../AreaChart/data";
import { negativeBar, secondaryYAxisType } from "../../BarCharts/BarChart/data";
import ProTip from "../../../components/ProTip";
import { NavLink } from "react-router-dom";
import { AppContext } from "../../../AppContext";
import { appConstants, domain, lineStyle } from "../../../constants";
import { headers as areaHeaders } from "../AreaChart/body";

const yAxisSidePropText = `yAxisSide={yAxisSides.RIGHT}`;
const curveTypeText = `curveType={CurveType.QUADRATIC}`;

export const headers = {
  minimal: { id: "minimal", label: "Minimal chart" },
  styling: { id: "styling", label: "Styling" },
  prefixSuffix: { id: "prefixSuffix", label: "Prefix Suffix" },
  curved: { id: "curved", label: "Curved" },
  labelled: { id: "labelled", label: "Labelled" },
  animated: { id: "animated", label: "Animated" },
  multiple: { id: "multiple", label: "Multiple Lines" },
  negative: { id: "negative", label: "Negative values" },
  lineChartBicolor: { id: "lineChartBicolor", label: "lineChartBicolor" },
  differentWaysToMultiColor: {id:"differentWaysToMultiColor", label:"Different Ways To MultiColor"},
  colors: { id: "colors", label: "colors" },
  multicolor: { id: "multicolor", label: "Multicolor" },
  lineSegments: {id: "lineSegments", label: "Line Segments" },
  highlightedRange: {id:"highlightedRange", label: "Highlighted Range" },
  customDataPoints: { id: "customDataPoints", label: "Custom Data Points" },
  arrow: { id: "arrow", label: "The Arrow" },
  scrollRef: { id: "scrollRef", label: "scrollRef" },
  focus: { id: "focus", label: "Focus" },
  secondaryYAxis: { id: "secondaryYAxis", label: "Secondary Y Axis" },
  secondaryData: { id: "secondaryData", label: "secondaryData" },
  animateOnChange: { id: "animateOnChange", label: "Animate On Data Change" },
  yAxisOffset: { id: "yAxisOffset", label: "yAxisOffset" },
};

export const MainBody = (props) => {
  const { isMobile, onBodyScroll } = props;
  const appContext = useContext(AppContext);
  const { width, theme } = appContext;
  const { sideDrawerWidth, rightNavWidth } = appConstants;

  const isSideDrawerVisible = width > 1100;

  const mainBodyWidth = isMobile
    ? width
    : width - rightNavWidth - (isSideDrawerVisible ? sideDrawerWidth - 10 : 30)

  const [isPlaying, setIsplaying] = useState(true);
  const videoRef = useRef(null);
  const onplayClick = () => {
    if (isPlaying) {
      videoRef.current.pause();
      setIsplaying(false);
    } else {
      videoRef.current.play();
      setIsplaying(true);
    }
  };
  const onVideoEnd = () => {
    setIsplaying(false);
  };

  const [isPlaying2, setIsplaying2] = useState(true);
  const videoRef2 = useRef(null);
  const onplayClick2 = () => {
    if (isPlaying2) {
      videoRef2.current.pause();
      setIsplaying2(false);
    } else {
      videoRef2.current.play();
      setIsplaying2(true);
    }
  };
  const onVideoEnd2 = () => {
    setIsplaying2(false);
  };

  return (
    <div
      className={styles.container}
      style={{ paddingLeft: isMobile ? 3 : 20, width: mainBodyWidth }}
      onScroll={onBodyScroll}
    >
      <h2>
        Line Charts
        <a
          style={{ marginLeft: 20, fontSize: 15, color: theme==='dark'? 'skyblue' : 'blue' }}
          target="_blank"
          href="https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/blob/master/docs/LineChart/LineChartProps.md"
          rel="noreferrer"
        >
          props table
        </a>
      </h2>
      <div style={{ marginLeft: 10, marginBottom: 100 }}>
        <h4 id={headers.minimal.id}>Minimal Line Chart</h4>
        <div style={{ marginLeft: 10 }}>
          The simplest Bar chart can be made with just-
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="javascript"
          >
            {data[0].code}
          </SyntaxHighlighter>
          <h5>Output-</h5>
          <img src={data[0].imgSrc} className={styles.barOutput} alt="" />
        </div>

        <h4 id={headers.styling.id}>Styling the Chart</h4>
        <div style={{ marginLeft: 10 }}>
          {`In the above code snippet, at line number 5, add the prop `}
          <span
            style={{
              fontStyle: "italic",
              fontSize: 16,
              fontWeight: "bold",
              color: "#177AD5",
            }}
          >{`color={'#177AD5'}`}</span>
          <p>This will result in-</p>
          <img
            src={require("../../../assets/lblue.png")}
            className={styles.barOutput}
            alt=""
          />

          <p>
            Adding the color prop changes the color of the line, similary adding
            the <b>thickness</b> changes the thickness of the line, and adding
            the <b>dataPointsColor</b> changes the color of the line{" "}
          </p>
          <p>
            The new code after adding the color, thickness and the
            dataPointsColor props-
          </p>

          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="javascript"
          >
            {data[1].code}
          </SyntaxHighlighter>
          <h5>Output-</h5>
          <img src={data[1].imgSrc} className={styles.barOutput} alt="" />

          <hr />
          <br />

          <p>
            Now that we know styling the chart, let's try making the below line
            chart-
          </p>
          <img src={data[2].imgSrc} className={styles.barOutput} alt="" />
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="javascript"
          >
            {data[2].code}
          </SyntaxHighlighter>
        </div>
        <hr />
        <h4 id={headers.prefixSuffix.id}>Prefix and Suffix</h4>
        <div style={{ marginLeft: 10 }}>
          Sometimes, the y-axis has values like- $10, $20, $30... or $10k, $20k,
          $30k... <br />
          The data for the above cases would have been [10, 20, 30...].
          <br />
          <br />
          We can use the prop <b>yAxisLabelPrefix</b> to add the prefix-{" "}
          <b>`$`</b> (or any other prefix) before each label on the y-axis.
          <br />
          Similary, we can use the <b>yAxisLabelSuffix</b> prop to add a suffix
          after each label on the y-axis.
        </div>
        <br />
        <ProTip
          body={() => {
            return (
              <>
                All properties related to the axes and axes labels are common
                across Bar, Line and Area charts. Visit the
                <NavLink
                  style={{ color: theme==='dark'? 'skyblue' : 'blue', marginLeft: 4, marginRight: 4 }}
                  to="/barchart"
                >
                  Bar charts page
                </NavLink>
                to see all the axes related properties.
              </>
            );
          }}
        />
        <h4 id={headers.curved.id}>Curved Line Chart</h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            Adding the prop <b>curved</b> turns the line into a curve. The above
            chart after adding the <i>curved</i> prop-
          </p>
          <img
            src={require("../../../assets/lineCurved.png")}
            className={styles.barOutput}
            alt=""
          />
          <h4>curveType and curvature</h4>
          <p>Currently, we support 2 types of curves-</p>
          <ol>
            <li>Cubic bezier curve</li>
            <li>Quadratic bezier curve</li>
          </ol>
          The default curveType is cubiz bezier curve. To change it to
          quadratic, pass the prop <b>{curveTypeText}</b>
          <p>
            The <b>curvature</b> takes a value between 0 and 1. The default
            curvature value is 0.2
            <br />
            curvature works only for cubic bezier curves. When curvature value
            is 0, the curve becomes straight line.
          </p>
        </div>

        <hr />

        <h4 id={headers.labelled.id}>Labelled Line Chart</h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            Adding the property <b>dataPointText</b> in the data array displays
            labels beside the data points.
          </p>
          <img
            src={require("../../../assets/lineLabelled.png")}
            className={styles.barOutput}
            alt=""
          />

          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="javascript"
          >
            {data[3].code}
          </SyntaxHighlighter>

          <h4>showValuesAsDataPointsText</h4>
          <p>
            In the above example, we can see that the dataPointText is same as
            value of the data point. In such a case we can simply pass the prop{" "}
            <b>showValuesAsDataPointsText</b> and then we will not have to add
            the dataPointText property to each data item.
          </p>
        </div>

        <hr />
        <br />
        <h4 id={headers.animated.id}>Animated Line Chart</h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            Adding the prop <b>isAnimated</b> to the {`<LineChart/>`} component
            renders the chart with an animation effect.
          </p>

          <video
            ref={videoRef}
            muted
            autoPlay
            onEnded={onVideoEnd}
            className={styles.outputBig}
            src={require("../../../assets/lining.mov")}
          />

          <p style={{ fontSize: 24 }}>
            {isPlaying ? (
              <FaPause onClick={onplayClick} />
            ) : (
              <FaPlay onClick={onplayClick} />
            )}
          </p>
        </div>
        <hr />
        <br />
        <h4 id={headers.multiple.id}>Multiple Lines</h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            Line charts are often used to compare 2 or more data sets. Below is
            an example -
          </p>
          <img src={data[4].imgSrc} className={styles.outputBig} alt="" />
          <p>
            For such type of charts, we pass 2 data sets named <b>data</b> and{" "}
            <b>data2</b>
            <br />
            Here is the code for the above chart -
          </p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="javascript"
          >
            {data[4].code}
          </SyntaxHighlighter>
        </div>

        <hr />
        <br />

        <h3 id={headers.negative.id}>Negative values</h3>
        <div style={{ marginLeft: 10, marginBottom: 20 }}>
          <p>Some charts have negative data values too. For example-</p>
          <img
            src={
              "https://github.com/Abhinandan-Kushwaha/chart-images/blob/main/screenshots/lineNeg.png?raw=true"
            }
            className={styles.outputBig}
            alt=""
          />
          <p>The code for the above chart is-</p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="javascript"
          >
            {negativeBar.replaceAll("BarChart", "LineChart")}
          </SyntaxHighlighter>
          <p>While using negative values, you may need to pass these props-</p>
          <ol>
            <li>maxValue</li>
            <li>mostNegativeValue</li>
            <li>stepValue</li>
            <li>stepHeight</li>
            <li>height</li>
            <li>noOfSections</li>
            <li>noOfSectionsBelowXAxis</li>
          </ol>
          <p>
            <b>mostNegativeValue</b> here represents the most negative value to
            be seen on the Y-Axis.
            <br />
            <b>noOfSections</b> means the number of horizontal sections (strips)
            above the X-Axis in the chart.
            <br />
            <b>noOfSectionsBelowXAxis</b> means the number of horizontal
            sections (strips) below the X-Axis in the chart.
            <br />
            <b>height</b> represents the height of the chart above the X-Axis (
            <b>
              <i>excluding</i>
            </b>{" "}
            the part below the X-Axis).
            <br />
          </p>
          <h3>All negative values</h3>
          <p>
            In case there are only negative values in your data set, and no
            positive values, your entire chart will appear below the X axis - in
            the fourth quadrant.
            <br />
            But you may want them to appear above the X axis - in the first
            quadrant (since all of them are negative).
            <br />
            For this, you can set the data values as positive (their absolute
            values), and add the prefix "<b>-</b>" with the help of the{" "}
            <b>yAxisLabelPrefix</b> prop.
          </p>
        </div>
        <hr />
        <br />

        <h3 id={headers.lineChartBicolor.id}>LineChartBicolor</h3>
        <div style={{ marginLeft: 10 }}>
          <p>
            Sometimes you may need a chart with 2 different set of colors- one
            for the positive values and the other for the negative values.
            <br />
            For example the below chart has everything below 0 is styled in a
            red area and everything above in green.
          </p>
          <img
            src="https://user-images.githubusercontent.com/13629606/172202911-920aefa7-1a78-4e5e-91d5-d3670e00fe45.png"
            className={styles.barOutput}
            alt=""
          />
          <p>
            To render such charts, we can use the <b>{`<LineChartBicolor>`}</b>{" "}
            component.
            <br />
            For example-
          </p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="javascript"
          >
            {lineChartBiColor}
          </SyntaxHighlighter>
          <p>
            Curved lines are not yet supported in such charts. Also, we can only
            render a single data set (multiple lines not yet supported).
            However, props like <b>areaChart, isAnimated</b> etc are supported.
            <br />
            This chart supports most of the props from the{" "}
            <b>{`<LineChart>`}</b> component. (excluding those which might
            request for unsupported features, like -{" "}
            <b>
              <i>curved, data2, data3, color2, color3</i>
            </b>{" "}
            etc).
            <br />
            <br />
            The props like-
            <ol>
              <li>color</li>
              <li>startFillColor</li>
              <li>endFillColor</li>
              <li>startOpacity</li>
              <li>endOpacity</li>
            </ol>
            represent the properties of the chart portion lying above the X
            axis.
            <br />
            <br />
            The respective properties of the chart portion lying below the X
            axis are obtained by appending the{" "}
            <b>suffix Negative to the prop names</b>.<br />
            Hence the prop name for the portion below the X axis become -
            <ol>
              <li>colorNegative</li>
              <li>startFillColorNegative</li>
              <li>endFillColorNegative</li>
              <li>startOpacityNegative</li>
              <li>endOpacityNegative</li>
            </ol>
          </p>
          <p>
            For more details, please see the{" "}
            <a
              target="_blank"
              style={{color: theme==='dark'? 'skyblue' : 'blue'}}
              href="https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/blob/master/docs/LineChart/LineChartBicolorProps.md"
            >
              LineChartBicolor props
            </a>
          </p>
        </div>

        <hr />
        <br />

        <h3 id={headers.differentWaysToMultiColor.id}>{headers.differentWaysToMultiColor.label}</h3>
        <div style={{ marginLeft: 10 }}>
          <p>
            We have props like color, color1, color2 etc to apply different colors to different lines. We can also add the color property inside the dataSet. <br /> <br />
            But what if we want different colors in the same line? For that, again, we have multiple ways-

            <ul>
              <li>colors</li>
              <li>lineGradientComponent</li>
              <li>lineSegments</li>
              <li>highlightedRange</li>
            </ul>
          </p>
          <p>
            All of these serve different purposes, and have been explained above. Let's understand how are they different from each other.
            <ul>
              <li><b>colors</b> prop is used to apply different colors to the parts of line lying between the given values. The change in color is discrete and in vertical direction.</li>
              <li><b>lineGradientComponent</b> prop is used to apply gradient effect to the line. The change in color is continuous (smooth) and can be in both vertical and horizontal directions.</li>
              <li><b>lineSegments</b> prop is used to change color as well as other properties (like thickness, strokeDashArray, curvature etc) between the given indices. The change is discrete and in horizontal direction.</li>
              <li><b>highlightedRange</b> prop is used to change color as well as other properties (like thickness, strokeDashArray, curvature etc) to a part of the line lying between the given values. The change in color is discrete and in vertical direction.</li>
            </ul>
            <div>
              Each of the above has been discussed in detail below. 
            </div>
          </p>
        </div>

        <hr />
        <br />

        <h3 id={headers.colors.id}>{headers.colors.label}</h3>
        <div style={{ marginLeft: 10 }}>
          <p>
            <b>colors</b> prop is used to apply different colors to the parts of line lying between the given values. The change in color is discrete and in vertical direction.
            <b>colors</b>, which accepts an array of objects of type <i>ColorFromToY</i> described below-

            <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="typescript"
          >
            {colorsType}
          </SyntaxHighlighter>
          Here's an example-
          </p>
          <img src={data[8].imgSrc} className={styles.barOutput} alt="" />
          <p>
            The code for the above chart is-
            <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="typescript"
          >
            {data[8].code}
          </SyntaxHighlighter>
          </p>

        </div>

        <hr />
        <br />

        <h3 id={headers.multicolor.id}>Multicolor line chart</h3>
        <div style={{ marginLeft: 10 }}>
          <p>
            Sometimes you may need a line chart whose line is a blend of 2 or
            more colors in form of gradient.
            <br />
            Here's an example-
          </p>
          <img
            src="https://user-images.githubusercontent.com/32467042/179031610-200fad04-20c5-4af1-9fe8-9d818165f95f.png"
            className={styles.longChart}
            alt=""
          />
          <p>
            Before we try to draw a chart like above, let's first render a
            minimal multicolor line chart like this-
          </p>
          <img
            src="https://user-images.githubusercontent.com/20596944/279773819-7dcc9cc7-2948-4eae-b391-beeeeaa20c05.png"
            className={styles.outputBig}
            alt=""
          />
          <p>The code for the above chart is-</p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="javascript"
          >
            {`<LineChart
  data={lineData}
  spacing={10}
  hideDataPoints
  lineGradient
/>`}
          </SyntaxHighlighter>
          <p>
            Notice that the line gets a gradient of <b>lightgreen</b> and{" "}
            <b>orange</b> color by default. These can be customised using the{" "}
            <b>lineGradientStartColor</b> and <b>lineGradientEndColor</b> props
            respectively.
            <br />
            We can use our own gradient component via the{" "}
            <b>lineGradientComponent</b> prop. Note that <b>lineGradient</b>{" "}
            prop is mandatory when using custom gradient component.
            <br />
            <br />
            Here's an example of custom lineGradientComponent-
          </p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="javascript"
          >
            {multiColorCustomGradientLineChart}
          </SyntaxHighlighter>
          <p>
            <b>LinearGradient</b> and <b>Stop</b> are imported from{" "}
            <i>react-native-svg</i>
          </p>

          <p>The output of the above code is-</p>
          <img
            src="https://user-images.githubusercontent.com/20596944/279775310-f4066888-2426-4d68-940a-c316d4214223.png"
            className={styles.outputBig}
            alt=""
          />
          <p>props used to render multicolor line charts are-</p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="typescript"
          >
            {`lineGradient?: boolean;
lineGradientComponent?: () => any;
lineGradientId?: string;
lineGradientDirection?: string;
lineGradientStartColor?: string;
lineGradientEndColor?: string;`}
          </SyntaxHighlighter>
          <a
            style={{color: theme==='dark'? 'skyblue' : 'blue'}}
            target="_blank"
            href="https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/issues/251"
          >
            See related issue
          </a>
        </div>

        <hr />
        <br />

        <h3 id={headers.lineSegments.id}>{headers.lineSegments.label}</h3>
        <div style={{ marginLeft: 10 }}>
          <p>
            Sometimes, we may want our line to have multiple colors but not in a gradient manner.<br />
            Or we may want our line to have gaps or dots for a given range or segment.<br />
            For such use cases, we can use the <b>lineSegments</b> prop.
            <br /><br />
            Here's an example-
          </p>
          <img
            src={require('../../../assets/segmented.png')}
            className={styles.outputBig}
            alt=""
            />
          <p>
            The code for the above chart is-
          </p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="typescript"
          >
            {`<LineChart
  data={[{value: 5}, {value: 8}, {value: 7}, {value: 4}, {value: 6}]}
  lineSegments={[
    {startIndex: 0, endIndex: 1, color: 'red'},
    {startIndex: 1, endIndex: 2, color: 'green', strokeDashArray: [4, 1]},
    {startIndex: 2, endIndex: 3, color: 'orange'},
  ]}
/>`}
          </SyntaxHighlighter>
          <p>
            The <b>lineSegments</b> prop is an array whose objects have the below properties-
          </p>
          <ul>
            <li>startIndex</li>
            <li>endIndex</li>
            <li>color</li>
            <li>thickness</li>
            <li>strokeDashArray</li>
          </ul>
          <p>
            See <a
            style={{color: theme==='dark'? 'skyblue' : 'blue'}}
            href='https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/issues/432#issuecomment-1820013349'
            target="_blank"
            >
              this issue on Github
            </a>
          </p>
          <h3>
            Line with breaks in between
          </h3>
          <p>
            The <b>lineSegments</b> prop can also be used to create breaks in the Line by making a segment of the line invisible.<br />
            A segment can be made invisible either by setting its color to <b>transparent</b> or by giving a zero width
          </p>
          <p>
            Here's an example-
          </p>
          <img src={data[5].imgSrc} className={styles.barOutput} alt="" />
          <p>
            The code for the above chart is
          </p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="javascript"
          >
            {data[5].code}
          </SyntaxHighlighter>
          <p>
            See <a
            style={{color: theme==='dark'? 'skyblue' : 'blue'}}
            href='https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/issues/100#issuecomment-1911135138'
            // target="_blank"
            >
              this issue on Github
            </a>
          </p>

          <h3 id={headers.highlightedRange.id}>
            {headers.highlightedRange.label}
          </h3>
          <p>
            We may need a line chart to be of a particular color or style if it lies within a given range.<br/>
            Here's an example-
          </p>
          <img
          src = 'https://i.stack.imgur.com/49aYe.png'
          className={styles.outputBig}
          alt = '' />
          <p>
            In the above chart, the part of the line that lies between the values <b>5</b> and <b>12</b> is colored <span style={{color:'green'}}>green</span>, and rest is colored <span style={{color:'red'}}>red</span>.
          </p>
          <p>
            This can be achieved with the help of the <b>highlightedRange</b> prop.<br />
            The code for the above chart is-
          </p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="javascript"
          >
            {highlightedRange}
          </SyntaxHighlighter>
          <p>
            The <b>highlightedRange</b> prop is an object which has the below properties-
          </p>
          <ul>
            <li>from</li>
            <li>to</li>
            <li>color</li>
            <li>thickness</li>
            <li>strokeDashArray</li>
          </ul>
          <p>
            See <a
            style={{color: theme==='dark'? 'skyblue' : 'blue'}}
            href='https://stackoverflow.com/a/77545985/7533126'
            target="_blank"
            >
              this question on Stack overflow
            </a>
          </p>
        </div>

        <hr />
        <br />

        <h4 id={headers.customDataPoints.id}>
          Custom Data Points and Vertical strip
        </h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            Normally, the data points can be circular (default) or rectangular
            in shape. Their size and color can be controlled through props.
            <br />
            Adding the <b>customDataPoint</b> prop allows you to use your own
            components as the data points.
            <br /> Similarly, adding the <b>dataPointLabelComponent</b> prop
            allows you to use your own components as the data point labels.
          </p>
          <img src={areaData[4].imgSrc} className={styles.outputBig} alt="" />
          <br />
          <p>
            Detailed explanation for this is given in the{" "}
            <a style={{color: theme==='dark'? 'skyblue' : 'blue'}} href={`${domain}areachart/#${areaHeaders.customDataPoints.id}`}>
              <b>Area Chart</b> section.
            </a>
          </p>
        </div>
        <hr />
        <br />

        <h4 id={headers.arrow.id}>The Arrow</h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            Some Line charts have an arrow at the end of the line. Here's an
            example-
          </p>
          <img
            src="https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/raw/master/demos/lineArrow.png"
            className={styles.barOutput}
            alt=""
          />
          <p>
            To display the arrrow at the end of the line, just pass the{" "}
            <b>showArrows</b> or <b>showArrow&#123;n&#125;.</b> prop. The
            properties of the arrow can controlled with the <b>arrowConfig</b>{" "}
            prop.
          </p>
        </div>
        <h4>Understanding the arrow-</h4>
        <div style={{ marginLeft: 10 }}>
          <img
            src="https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/raw/master/demos/arrowProps.png"
            className={styles.outputBig}
            alt=""
          />
          <p>
            The <b>arrowConfig</b> has the properties allowed by the{" "}
            <b>arrowType</b>-
          </p>
          <table>
            <tr>
              <th>prop</th>
              <th>type</th>
              <th>default</th>
            </tr>
            <tr>
              <td>length</td> <td>number</td>
              <td>10</td>
            </tr>
            <tr>
              <td>width</td> <td>number</td>
              <td>10</td>
            </tr>
            <tr>
              <td>strokeWidth</td> <td>number</td>
              <td>thickness1</td>
            </tr>
            <tr>
              <td>strokeColor</td> <td>string</td>
              <td>color1</td>
            </tr>
            <tr>
              <td>fillColor</td> <td>string</td>
              <td>'none'</td>
            </tr>
            <tr>
              <td>showArrowBase</td> <td>boolean</td>
              <td>true</td>
            </tr>
          </table>
          <h4>showArrowBase</h4>
          <p>The arrow base can be understood with the pic below-</p>
          <img
            src="https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/raw/master/demos/arrowBase.png"
            className={styles.barOutput}
            alt=""
          />
          <p>
            The default value of <b>showArrowBase</b> is true. To fill the arrow
            with <b>fillColor</b>, it is mandatory to have <b>showArrowBase</b>{" "}
            true. The arrow can't be filled if it's baseless.
          </p>
        </div>
        <hr />
        <br />

        <h4 id={headers.scrollRef.id}>
          scrollRef, scrollToEnd and scrollToIndex
        </h4>
        <div style={{ marginLeft: 10 }}>
          <ol>
            <li>
              <b>scrollRef</b> You can create a ref using React.useRef() and
              pass it into the scrollRef prop. Now the ref can be used to scroll
              to a given position.
            </li>
            <li>
              <b>scrollToEnd</b> scrolls to the last data point
            </li>
            <li>
              <b>scrollToIndex</b> scrolls to the given data point
            </li>
          </ol>
        </div>
        <video
          ref={videoRef2}
          muted
          autoPlay
          onEnded={onVideoEnd2}
          className={styles.outputBig}
          src={require("../../../assets/scrollRef.mp4")}
        />

        <p style={{ fontSize: 24 }}>
          {isPlaying2 ? (
            <FaPause onClick={onplayClick2} />
          ) : (
            <FaPlay onClick={onplayClick2} />
          )}
        </p>
        <p>
          <b>
            <a
              style={{color: theme==='dark'? 'skyblue' : 'blue'}}
              target="_blank"
              href="https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/issues/301"
            >
              Here is the code
            </a>{" "}
            for the above chart using using scrollRef to control the scroll of
            the chart from an external component.
          </b>
        </p>

        <hr />

        <h4 id={headers.focus.id}>
          Enabling focus, and focusing the respective data point
        </h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            Many Line charts, specially those in trading apps, allow users to
            press on any part of the chart, and highlight the respective data
            point.
          </p>
          <img
            src={require("../../../assets/focusPoint.gif")}
            style={{ height: 400 }}
            alt=""
          />
          <p>
            This can be achieved using the <b>focusEnabled, showStripOnFocus</b>{" "}
            and <b>showTextOnFocus</b> props.
            <br />
            <br />
          </p>
          <p>
            In addition to just highlighting the pressed data point, you can
            pass any callback function to the press event using the{" "}
            <b>onFocus</b> prop.
          </p>
          <p>
            The properties of the focused data point can be configured using
            these props-
            <ul>
              <li>
                <b>focusedDataPointShape</b>
              </li>
              <li>
                <b>focusedDataPointWidth</b>
              </li>
              <li>
                <b>focusedDataPointHeight</b>
              </li>
              <li>
                <b>focusedDataPointColor</b>
              </li>
              <li>
                <b>focusedDataPointRadius</b>
              </li>
              <li>
                <b>focusedCustomDataPoint</b>
              </li>
            </ul>
          </p>
          <p>
            Advanced focus related props -
          </p>
          <ul>
            <li>
              <b>focusTogether</b>
            </li>
            <li>
              <b>focusProximity</b>
            </li>
            <li>
              <b>showDataPointOnFocus</b>
            </li>
            <li>
              <b>showDataPointLabelOnFocus</b>
            </li>
            <li>
              <b>showTextOnFocus</b>
            </li>
          </ul>
          <p>
            See the entire list of {' '}
            <a
              style={{color: theme==='dark'? 'skyblue' : 'blue'}}
              target="_blank"
              rel="noreferrer"
              href = "https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/blob/master/docs/LineChart/LineChartProps.md#onfocus-and-strip-related-props"
              alt="focusing">
                Focus related props here.
              </a>
          </p>
          <p>
            <span className={styles.noteText}>Note:</span> Some props have been
            renamed in version <strong>1.3.2</strong>
            <br />
            Here is the list of prop names changed in version{" "}
            <strong>1.3.2</strong>-
          </p>
          <table>
            <tr>
              <th>Prop name prior to version 1.3.2</th>
              <th>Prop name in and after 1.3.2</th>
            </tr>
            <tr>
              <td>pressEnabled</td>
              <td>focusEnabled</td>
            </tr>
            <tr>
              <td>onPress</td>
              <td>
                onFocus (onPress also exists but has diffferent functionality)
              </td>
            </tr>
            <tr>
              <td>showDataPointOnPress</td>
              <td>showDataPointOnFocus</td>
            </tr>
            <tr>
              <td>showStripOnPress</td>
              <td>showStripOnFocus</td>
            </tr>
            <tr>
              <td>showTextOnPress</td>
              <td>showTextOnFocus</td>
            </tr>
          </table>
          <p>
            <b>
              Read {' '}
              <a
              style={{color: theme==='dark'? 'skyblue' : 'blue'}}
              target="_blank"
              rel="noreferrer"
              href = "https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/blob/master/docs/dev/LineChart/Focusing.md"
              alt="focusing">
                more about Focus.
              </a>
            </b>
          </p>
        </div>

        <br />
        <hr />
        <br />
        <h4 id={headers.secondaryYAxis.id}>Y axis on both sides</h4>
        <div style={{ marginLeft: 10, marginBottom: 20 }}>
          We can render the Y axis on the right side of the chart using{" "}
          <b>{yAxisSidePropText}</b>
          <br />
          But, if we need Y axis on both left and right sides, we can use the{" "}
          <b>secondaryYAxis</b> prop.
          <br />
          <br />
          <b>secondaryYAxis</b> is an object of type of{" "}
          <b>secondaryYAxisType</b>. You can control the properties of secodary
          Y axis by passing an object of properties. Following is the type
          definition of the secondaryYAxisType object
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="typescript"
          >
            {secondaryYAxisType}
          </SyntaxHighlighter>
          <span className={styles.noteText}>Note: </span> secondaryYAxis can
          also be passed a boolean value. When passed true, it just renders a
          secondary Y axis on the right side identical to the one on the left.
          <h4 id={headers.secondaryData.id}>
            secondaryData and secondaryLineConfig
          </h4>
          <div>
            <p>
              Although we can render the second line using the <b>data2</b>{" "}
              prop, but the values of the second line rendered using data2 will
              be referenced from the same Y axis.
              <br />
              <br />
              Sometimes we may need our second line to correspond to the Y axis
              on the right side. Then we should use the <b>
                secondaryData
              </b> and <b>secondaryYAxisType</b> props.
              <br />
              The properties of the secondary line are control using the{" "}
              <b>secondaryLineConfig</b> prop.
            </p>
            <p>
              Here is an example of a Line chart using 2 Y axes and a 2 lines-
            </p>

            <img src={data[6].imgSrc} style={{ height: 280 }} alt="" />
            <p>The code for the above chart is-</p>
            <SyntaxHighlighter
              showLineNumbers
              lineNumberStyle={lineStyle}
              language="javascript"
            >
              {data[6].code}
            </SyntaxHighlighter>
          </div>
        </div>

        <hr />
        <br />
        <h4 id={headers.animateOnChange.id}>Animate on data change</h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            To make your data representation outstanding, you can consider
            adding <b>smooth transitions</b> to your chart on <i>data change</i>
            .
            <br />
            <br />
            Here's an example-
          </p>
          <img src={data[7].imgSrc} style={{ height: 280 }} alt="" />
          <p>
            Surprisingly, this huge feat can be achieved just by adding a prop
            called <b>animateOnDataChange</b>. And the duration of the
            transition animation can be controlled using the{" "}
            <b>onDataChangeAnimationDuration</b>prop.
            <br />
            <br />
          </p>

          <p>
            <span className={styles.noteText}>Note:</span> Currently, this
            transition animation is{" "}
            <b>
              not supported for <i>curved</i>
            </b>{" "}
            Line or Area charts. Also, in case of multiple data lines, the
            transition animation will only be applicable to the first data line.
          </p>

          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="javascript"
          >
            {data[7].code}
          </SyntaxHighlighter>
          <p>
            Now, whenever the values in the data <i>(currentData)</i> change,
            you can see it transition smoothly.
            <br />
            <br />
            To test it, you can change the values in the data on some event,
            like on press of a button.
          </p>
        </div>
        <hr />
        <h4 id={headers.yAxisOffset.id}>yAxisOffset</h4>
        <div style={{ marginLeft: 10, marginBottom: 20 }}>
          <p>Look at the chart below.</p>
          <img
            src="https://user-images.githubusercontent.com/20596944/155423954-4ad44c68-01a1-4622-98a7-7573aa1b13b0.png"
            className={styles.barOutput}
            alt=""
          />
          <p>
            It has no use for values under 100, it's making the chart unreadable
            so it's better to limit the range on the Y Axis.
          </p>
          <p>The data for the above chart is-</p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="javascript"
          >
            {specialData}
          </SyntaxHighlighter>
          <p>
            Now, you want the values to start from 100, and they range between
            100 to 200.
            <br />
            <br />
            We can use the prop <b>yAxisOffset</b> to achieve this.
          </p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language="javascript"
          >
            {specialCode}
          </SyntaxHighlighter>
          <p>Now the chart looks like this-</p>
          <img
            src="https://user-images.githubusercontent.com/20596944/155424192-a329b495-2666-4069-a361-dacd97def28d.png"
            className={styles.barOutput}
            alt=""
          />
          <hr />
          <br />
        </div>
      </div>
    </div>
  );
};
